import type { FC } from 'react'
import React, { useState } from 'react'
import { makeStyles, createStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import { cloneDeep } from 'lodash'
import IconButton from '@material-ui/core/IconButton'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import FormGroup from '@material-ui/core/FormGroup'
import Collapse from '@material-ui/core/Collapse'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'
import type { ITheme } from '@homeviews/styles'

import { Typography, Checkbox, Tooltip } from '@homeviews/shared-ui'
import { updateObjectProp } from '@homeviews/utils'

const TOOLTIPS = {
  USER_PERMISSIONS: (
    <div className="c-tooltipContent c-tooltipContent--dark">
      <Typography>
        Select which permissions your team have access to.
      </Typography>
    </div>
  ),
  CREATE_COMPANY_WIDGETS: (
    <div className="c-tooltipContent c-tooltipContent--dark">
      <Typography>
        Generate overall company star rating widgets to use.
      </Typography>
    </div>
  ),
  EDIT_COMPANY_PAGE: (
    <div className="c-tooltipContent c-tooltipContent--dark">
      <Typography>
        This permission gives the user the ability to make changes to the
        overall company page. A user can add in new images, submit change
        requests to the description and add in a video.
      </Typography>
    </div>
  ),
  VIEW_MARKETING_MATERIALS: (
    <div className="c-tooltipContent c-tooltipContent--dark">
      <Typography>
        Allow this user to access your overall company marketing materials.
      </Typography>
    </div>
  ),
  CONTROL_SETTINGS: (
    <div className="c-tooltipContent c-tooltipContent--dark">
      <Typography variant="h2" style={{ color: '#000433' }}>
        Control Settings
      </Typography>
      <Typography variant="h3" style={{ color: '#000433' }}>
        View
      </Typography>
      <Typography>
        This permission allows the user to see the development in the dashboard.
        You must have this permission ticked for any other permissions and
        notifications for a development to work.
      </Typography>
      <Typography variant="h3" style={{ color: '#000433' }}>
        Manage
      </Typography>
      <Typography>
        This permission gives the user the ability to add and manage permissions
        for other users. For the given development, a manager will receive
        notifications to approve review responses if relevant and are able to
        edit their development, respond to reviews without needing approval,
        create widgets and view their development’s marketing materials.
      </Typography>
      <Typography variant="h3" style={{ color: '#000433' }}>
        Edit
      </Typography>
      <Typography>
        This permission allows the user to edit the relevant development page by
        updating the imagery, adding a video, submitting change requests to the
        copy and selecting the facilities.
      </Typography>
    </div>
  ),
  DEVELOPMENT_SETTINGS: (
    <div className="c-tooltipContent c-tooltipContent--dark">
      <Typography variant="h2" style={{ color: '#000433' }}>
        Development Settings Permissions
      </Typography>
      <Typography variant="h3" style={{ color: '#000433' }}>
        Respond to Reviews
      </Typography>
      <Typography>
        This permission allows the user to respond to the reviews for this given
        development.
      </Typography>
      <Typography variant="h3" style={{ color: '#000433' }}>
        Reply Without Moderation
      </Typography>
      <Typography>
        This permission gives the user the ability to respond to the reviews
        without needing a manager to approve them.
      </Typography>
      <Typography variant="h3" style={{ color: '#000433' }}>
        Create Widgets
      </Typography>
      <Typography>
        This permission gives the user the ability to create widgets for their
        development as well as at a company level.
      </Typography>
      <Typography variant="h3" style={{ color: '#000433' }}>
        Access to Marketing Material
      </Typography>
      <Typography>
        This allows the user to access marketing materials for their specific
        development.
      </Typography>
    </div>
  ),
  NOTIFICATIONS_SETTINGS: (
    <div className="c-tooltipContent c-tooltipContent--dark">
      <Typography variant="h2" style={{ color: '#000433' }}>
        New Review Notifications
      </Typography>
      <Typography>
        Select which type of review notification for this user. A user can
        receive all review notifications for their specific development, or just
        specific 4-5 Star Reviews or 1-3 Star Reviews for their development.
      </Typography>
    </div>
  ),
}

const useStyles = makeStyles((theme: ITheme) =>
  createStyles({
    root: {
      width: '100%',
      '& .MuiTableCell-head': {
        fontSize: '12px',
        fontWeight: 500,
        letterSpacing: '0.2px',
        lineHeight: '22px',
        color: theme.colors.grey600,
        padding: theme.spacing(0.75, 2),
        backgroundColor: theme.colors.grey200,
        borderRight: `1px solid ${theme.colors.grey250}`,
        position: 'relative',
        paddingRight: 20,
        '&:last-child': {
          borderRight: 'none',
        },
        '&:hover': {
          '& .l-sortArrowWrapper': {
            opacity: 1,
            transition: 'transform .3s ease-in-out, opacity .3s ease-in-out',
          },
        },
      },
      '& .MuiTableRow-root': {
        backgroundColor: theme.colors.white,
      },
      '& .MuiTableCell-body': {
        color: theme.colors.grey480,
        fontSize: 16,
        letterSpacing: 0,
        lineHeight: '22px',
        fontWeight: 400,
        padding: theme.spacing(0.9375, 2),
        wordBreak: 'break-word',
        '&:hover': {
          cursor: 'pointer',
        },
      },
      '& .sort_asc': {
        opacity: 1,
        transform: 'rotate(-180deg)',
        transition: 'transform .3s ease-in-out, opacity .3s ease-in-out',
      },
      '& .sort_desc': {
        opacity: 1,
        transform: 'rotate(0deg)',
        transition: 'transform .3s ease-in-out, opacity .3s ease-in-out',
      },
      '& .l-table__actionsCell': {
        display: 'flex',
      },
      '& .l-table__link': {
        '&:hover': {
          cursor: 'pointer',
        },
      },
      '& .l-table__link--delete': {
        color: theme.colors.red,
      },
      '& .l-table__link--primary': {
        color: theme.colors.primaryLink,
        marginRight: 20,
      },
      '& .l-table__cell--award': {
        display: 'flex',
        alignItems: 'center',
        '& img': {
          marginRight: 5,
          marginTop: 1,
        },
      },
      '& .l-table__imageWrapper': {
        position: 'relative',
        width: 85,
        height: 56,
        '& img': {
          objectFit: 'cover',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
        },
      },
    },
    noClickableRow: {
      '& .MuiTableCell-body': {
        '&:hover': {
          cursor: 'initial',
        },
      },
    },
    fontStyleSmall: {
      '& .MuiTableCell-body': {
        color: theme.colors.grey800,
        fontSize: 14,
      },
    },
    paper: {
      width: '100%',
      marginBottom: theme.spacing(2),
    },
    table: {
      minWidth: 750,
      boxShadow: '1px 3px 6px 0 rgba(0,0,0,0.05)',
      border: `1px solid ${theme.colors.grey250}`,
      '& .MuiFormControlLabel-label': {
        color: '#636B76',
        fontSize: theme.typography.pxToRem(12),
        fontWeight: '500',
        letterSpacing: '1px',
        lineHeight: '22px',
      },
    },
    sortArrowWrapper: {
      opacity: 0,
      transition: 'transform .3s ease-in-out, opacity .3s ease-in-out',
      position: 'absolute',
      top: 8,
      right: 10,
      height: 18,
      width: 18,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '&:hover': {
        cursor: 'pointer',
      },
    },
    globalPermissions: {},
    permissionsSectionTitle: {
      marginTop: 16,
      marginBottom: 10,
    },
    permissionsTableSectionTitle: {
      marginTop: 16,
      marginBottom: 20,
    },
    checkboxContainer: {
      display: 'flex',
      marginRight: 16,
      '& .MuiFormControlLabel-root': {
        marginRight: 5,
      },
    },
  }),
)

const labels = {
  EDIT_COMPANY_PAGE: 'Make changes to company page',
  EDIT_PERMISSIONS: 'Make changes to user permissions',
  VIEW_MARKETING_MATERIALS: 'Access all company marketing materials',
  VIEW_DEVELOPMENT: 'View',
  MANAGE_DEVELOPMENT: 'Manage',
  EDIT_DEVELOPMENT: 'Edit',
  REPLY_TO_REVIEWS: 'Respond to reviews',
  REPLY_TO_REVIEWS_WITHOUT_MODERATION: 'Reply without moderation',
  CREATE_WIDGETS: 'Create widgets',
  CREATE_COMPANY_WIDGETS: 'Create company widgets',
  VIEW_MARKETINGS_MATERIALS: 'Access to marketing materials',
  UNDERTAKE_RESIDENT_OUTREACH_REQUESTS: 'Undertake resident email requests',
  all_reviews: 'All reviews',
  positive_reviews: '4-5 star',
  negative_reviews: '1-3 star',
}

const headCells = [
  {
    id: 'SET_ALL',
    label: 'SET ALL',
  },
  {
    id: 'VIEW_DEVELOPMENT',
    label: 'ALL VIEW',
  },
  {
    id: 'MANAGE_DEVELOPMENT',
    label: 'ALL MANAGE',
  },
  {
    id: 'EDIT_DEVELOPMENT',
    label: 'ALL EDIT',
  },
]

interface IProps {
  permissions: any
  setPermissions: (permissions: any) => void
}

const PermissionsTable: FC<IProps> = ({ permissions, setPermissions }) => {
  const classes = useStyles()
  const [bulkPermissions, setBulkPermissions] = useState({
    VIEW_DEVELOPMENT: false,
    EDIT_DEVELOPMENT: false,
    MANAGE_DEVELOPMENT: false,
  })

  const handlePermissionChange = (accessor: string, value: boolean) => {
    const newPermissions = permissions ? cloneDeep(permissions) : {}

    updateObjectProp(newPermissions, value, accessor)

    setPermissions(newPermissions)
  }

  const toggleAll = (permission) => {
    const newPermissions = permissions ? cloneDeep(permissions) : {}

    for (const devId in permissions.developments) {
      updateObjectProp(
        newPermissions,
        !bulkPermissions[permission],
        `developments.${devId}.permissions.${permission}`,
      )
    }

    setPermissions(newPermissions)

    setBulkPermissions({
      ...bulkPermissions,
      [permission]: !bulkPermissions[permission],
    })
  }

  return (
    <div className={clsx(classes.root)}>
      <Typography variant="h3" className={classes.permissionsSectionTitle}>
        Global Permissions
      </Typography>
      <div className={classes.globalPermissions}>
        <FormGroup row>
          <div className={classes.checkboxContainer}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={permissions?.EDIT_COMPANY_PAGE}
                  onChange={() =>
                    handlePermissionChange(
                      'EDIT_COMPANY_PAGE',
                      !permissions?.EDIT_COMPANY_PAGE,
                    )
                  }
                  name="EDIT_COMPANY_PAGE"
                  value={permissions?.EDIT_COMPANY_PAGE}
                />
              }
              label={labels.EDIT_COMPANY_PAGE}
            />
            <Tooltip content={TOOLTIPS.EDIT_COMPANY_PAGE} layout="dark" />
          </div>
          <div className={classes.checkboxContainer}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={permissions?.EDIT_PERMISSIONS}
                  onChange={() =>
                    handlePermissionChange(
                      'EDIT_PERMISSIONS',
                      !permissions?.EDIT_PERMISSIONS,
                    )
                  }
                  name="EDIT_PERMISSIONS"
                  value={permissions?.EDIT_PERMISSIONS}
                />
              }
              label={labels.EDIT_PERMISSIONS}
            />
            <Tooltip content={TOOLTIPS.USER_PERMISSIONS} layout="dark" />
          </div>
          <div className={classes.checkboxContainer}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={permissions?.VIEW_MARKETING_MATERIALS}
                  onChange={() =>
                    handlePermissionChange(
                      'VIEW_MARKETING_MATERIALS',
                      !permissions?.VIEW_MARKETING_MATERIALS,
                    )
                  }
                  name="VIEW_MARKETING_MATERIALS"
                  value={permissions?.VIEW_MARKETING_MATERIALS}
                />
              }
              label={labels.VIEW_MARKETING_MATERIALS}
            />
            <Tooltip
              content={TOOLTIPS.VIEW_MARKETING_MATERIALS}
              layout="dark"
            />
          </div>
          <div className={classes.checkboxContainer}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={permissions?.CREATE_WIDGETS}
                  onChange={() =>
                    handlePermissionChange(
                      'CREATE_WIDGETS',
                      !permissions?.CREATE_WIDGETS,
                    )
                  }
                  name="CREATE_WIDGETS"
                  value={permissions?.CREATE_WIDGETS}
                />
              }
              label={labels.CREATE_COMPANY_WIDGETS}
            />
            <Tooltip content={TOOLTIPS.CREATE_COMPANY_WIDGETS} layout="dark" />
          </div>
        </FormGroup>
      </div>
      <Typography
        variant="h3"
        className={clsx(
          classes.permissionsSectionTitle,
          classes.permissionsTableSectionTitle,
        )}
      >
        Development Permissions
      </Typography>
      <TableContainer>
        <Table
          className={classes.table}
          aria-labelledby="tableTitle"
          size="small"
          aria-label="enhanced table"
          style={{
            tableLayout: 'fixed',
          }}
        >
          <TableHead>
            <TableRow>
              {headCells.map((cell, index) => {
                return (
                  <TableCell key={cell.id} colSpan={index === 0 ? 2 : 1}>
                    {index > 0 ? (
                      <FormGroup row>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={bulkPermissions[cell.id]}
                              onChange={() => toggleAll(cell.id)}
                              name={cell.id}
                              value={bulkPermissions[cell.id]}
                            />
                          }
                          label={cell.label}
                        />
                      </FormGroup>
                    ) : (
                      cell.label
                    )}
                  </TableCell>
                )
              })}
              <TableCell colSpan={1} />
            </TableRow>
          </TableHead>

          <TableBody>
            {!!Object.keys(permissions?.developments || {})?.length &&
              Object.keys(permissions.developments).map((devId) => {
                const row = {
                  id: devId,
                  development_name: permissions.developments[devId]?.name,
                  ...permissions.developments[devId]?.permissions,
                  ...permissions.developments[devId]?.review_notifications,
                }
                return (
                  <Row
                    data={row}
                    key={devId}
                    handleChange={handlePermissionChange}
                  />
                )
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  )
}

const useRowStyles = makeStyles(() =>
  createStyles({
    innerRow: {},
    innerTable: {
      backgroundColor: '#F1FBF9',
      '& .MuiTableRow-root': {
        backgroundColor: 'inherit',
      },
    },
    infoCellContainer: {
      display: 'flex',
      '& > span': {
        display: 'block',
        marginRight: 5,
      },
    },
  }),
)

function Row({ data, handleChange }) {
  const classes = useRowStyles()

  const [open, setOpen] = useState(false)

  return (
    <>
      <TableRow className={classes.innerRow}>
        <TableCell component="th" scope="row" colSpan={2}>
          <div className={classes.infoCellContainer}>
            <span>{data?.development_name}</span>
            <Tooltip
              content={TOOLTIPS.CONTROL_SETTINGS}
              layout="dark"
              noArrow
            />
          </div>
        </TableCell>
        <TableCell align="right">
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={data?.VIEW_DEVELOPMENT}
                  onChange={() =>
                    handleChange(
                      `developments.${data?.id}.permissions.VIEW_DEVELOPMENT`,
                      !data?.VIEW_DEVELOPMENT,
                    )
                  }
                  name="VIEW_DEVELOPMENT"
                  value={data.VIEW_DEVELOPMENT}
                />
              }
              label={labels.VIEW_DEVELOPMENT}
            />
          </FormGroup>
        </TableCell>
        <TableCell>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={data?.MANAGE_DEVELOPMENT}
                  onChange={() =>
                    handleChange(
                      `developments.${data?.id}.permissions.MANAGE_DEVELOPMENT`,
                      !data?.MANAGE_DEVELOPMENT,
                    )
                  }
                  name="MANAGE_DEVELOPMENT"
                  value={data.MANAGE_DEVELOPMENT}
                />
              }
              label={labels.MANAGE_DEVELOPMENT}
            />
          </FormGroup>
        </TableCell>

        <TableCell align="right">
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={data?.EDIT_DEVELOPMENT}
                  onChange={() =>
                    handleChange(
                      `developments.${data?.id}.permissions.EDIT_DEVELOPMENT`,
                      !data?.EDIT_DEVELOPMENT,
                    )
                  }
                  name="EDIT_DEVELOPMENT"
                  value={data.EDIT_DEVELOPMENT}
                />
              }
              label={labels.EDIT_DEVELOPMENT}
            />
          </FormGroup>
        </TableCell>
        <TableCell align="right">
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ padding: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <div className={classes.innerTable}>
              <Table
                size="small"
                style={{
                  tableLayout: 'fixed',
                }}
              >
                <TableBody>
                  <TableRow>
                    <TableCell component="th" scope="row" colSpan={2}>
                      <div className={classes.infoCellContainer}>
                        <span>Development</span>
                        <Tooltip
                          content={TOOLTIPS.DEVELOPMENT_SETTINGS}
                          layout="dark"
                        />
                      </div>
                    </TableCell>
                    <TableCell>
                      <FormGroup row>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={data?.REPLY_TO_REVIEWS}
                              onChange={() =>
                                handleChange(
                                  `developments.${data?.id}.permissions.REPLY_TO_REVIEWS`,
                                  !data?.REPLY_TO_REVIEWS,
                                )
                              }
                              name="REPLY_TO_REVIEWS"
                              value={data?.REPLY_TO_REVIEWS}
                            />
                          }
                          label={labels.REPLY_TO_REVIEWS}
                        />
                      </FormGroup>
                    </TableCell>
                    <TableCell>
                      <FormGroup row>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={
                                data?.REPLY_TO_REVIEWS_WITHOUT_MODERATION
                              }
                              onChange={() =>
                                handleChange(
                                  `developments.${data?.id}.permissions.REPLY_TO_REVIEWS_WITHOUT_MODERATION`,
                                  !data?.REPLY_TO_REVIEWS_WITHOUT_MODERATION,
                                )
                              }
                              name="REPLY_TO_REVIEWS_WITHOUT_MODERATION"
                              value={data?.REPLY_TO_REVIEWS_WITHOUT_MODERATION}
                            />
                          }
                          label={labels.REPLY_TO_REVIEWS_WITHOUT_MODERATION}
                        />
                      </FormGroup>
                    </TableCell>
                    <TableCell>
                      <FormGroup row>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={data?.CREATE_WIDGETS}
                              onChange={() =>
                                handleChange(
                                  `developments.${data?.id}.permissions.CREATE_WIDGETS`,
                                  !data?.CREATE_WIDGETS,
                                )
                              }
                              name="CREATE_WIDGETS"
                              value={data?.CREATE_WIDGETS}
                            />
                          }
                          label={labels.CREATE_WIDGETS}
                        />
                      </FormGroup>
                    </TableCell>
                    <TableCell>&nbsp;</TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell component="th" scope="row" colSpan={2}>
                      <div className={classes.infoCellContainer}>
                        <span>&nbsp;</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <FormGroup row>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={data?.VIEW_MARKETING_MATERIALS}
                              onChange={() =>
                                handleChange(
                                  `developments.${data?.id}.permissions.VIEW_MARKETING_MATERIALS`,
                                  !data?.VIEW_MARKETING_MATERIALS,
                                )
                              }
                              name="VIEW_MARKETING_MATERIALS"
                              value={data?.VIEW_MARKETING_MATERIALS}
                            />
                          }
                          label={labels.VIEW_MARKETING_MATERIALS}
                        />
                      </FormGroup>
                    </TableCell>
                    <TableCell>
                      <FormGroup row>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={
                                data?.UNDERTAKE_RESIDENT_OUTREACH_REQUESTS
                              }
                              onChange={() =>
                                handleChange(
                                  `developments.${data?.id}.permissions.UNDERTAKE_RESIDENT_OUTREACH_REQUESTS`,
                                  !data?.UNDERTAKE_RESIDENT_OUTREACH_REQUESTS,
                                )
                              }
                              name="UNDERTAKE_RESIDENT_OUTREACH_REQUESTS"
                              value={data?.UNDERTAKE_RESIDENT_OUTREACH_REQUESTS}
                            />
                          }
                          label={labels.UNDERTAKE_RESIDENT_OUTREACH_REQUESTS}
                        />
                      </FormGroup>
                    </TableCell>
                    <TableCell colSpan={2}>&nbsp;</TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell component="th" scope="row" colSpan={2}>
                      <div className={classes.infoCellContainer}>
                        <span>Review Notifications</span>
                        <Tooltip
                          content={TOOLTIPS.NOTIFICATIONS_SETTINGS}
                          layout="dark"
                        />
                      </div>
                    </TableCell>
                    <TableCell>
                      <FormGroup row>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={data?.all_reviews}
                              onChange={() =>
                                handleChange(
                                  `developments.${data?.id}.review_notifications.all_reviews`,
                                  !data?.all_reviews,
                                )
                              }
                              name="all_reviews"
                              value={data?.all_reviews}
                            />
                          }
                          label={labels.all_reviews}
                        />
                      </FormGroup>
                    </TableCell>
                    <TableCell>
                      <FormGroup row>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={data?.positive_reviews}
                              onChange={() =>
                                handleChange(
                                  `developments.${data?.id}.review_notifications.positive_reviews`,
                                  !data?.positive_reviews,
                                )
                              }
                              name="positive_reviews"
                              value={data?.positive_reviews}
                            />
                          }
                          label={labels.positive_reviews}
                        />
                      </FormGroup>
                    </TableCell>
                    <TableCell>
                      <FormGroup row>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={data?.negative_reviews}
                              onChange={() =>
                                handleChange(
                                  `developments.${data?.id}.review_notifications.negative_reviews`,
                                  !data?.negative_reviews,
                                )
                              }
                              name="negative_reviews"
                              value={data?.negative_reviews}
                            />
                          }
                          label={labels?.negative_reviews}
                        />
                      </FormGroup>
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

export default PermissionsTable
